<template>
	<div class="new-appointment-form">
		<v-form ref="form" v-model="valid">
			<v-container fluid>
				<v-row no-gutters>
					<v-col cols="12" sm="4">
						<v-autocomplete
							v-model="appointment.patient_id"
							:search-input.sync="search"
							:items="patients"
							:filter="patientFilter"
							item-text="name"
							item-value="id"
							:label="$gettext('Patient')"
							clearable
							outlined
							:no-data-text="$gettext('No Patients found')"
							prepend-inner-icon="person"
							class="px-2"
							required
							:rules="patientRules"
							@click:prepend-inner="goToPatient"
							@change="edited"
							id="patient-field"
							:disabled="!calendar"
							ref="patientField"
						>
							<v-list-item-group slot="no-data" v-if="search != null && search != ''">
								<v-list-item class="px-4">
									<v-list-item-content>
										<v-list-item-title @click.stop.prevent="newPatient(search)">
											{{ search }}
											<span v-translate>(New Patient)</span>
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
							</v-list-item-group>
						</v-autocomplete>
					</v-col>
					<v-col cols="12" sm="4">
						<v-select
							:items="specialitiesOptions"
							:label="$gettext('Specialty')"
							v-model="appointment.speciality_id"
							class="px-2"
							outlined
							required
							:rules="specialityRules"
							:no-data-text="$gettext('No Specialities available')"
							item-text="name"
							item-value="id"
							@change="edited"
						></v-select>
					</v-col>
					<v-col cols="12" sm="4">
						<v-select
							:items="professionalsOptions"
							:label="$gettext('Health Professional')"
							v-model="appointment.user_id"
							class="px-2"
							outlined
							:rules="professionalRules"
							:no-data-text="$gettext('No Health Professionals available')"
							item-text="name"
							item-value="id"
							@change="edited"
						></v-select>
					</v-col>
					<v-col cols="12" sm="4">
						<v-menu
							v-model="dateMenu"
							:close-on-content-click="false"
							:nudge-right="40"
							transition="scale-transition"
							offset-y
							min-width="290px"
						>
							<template v-slot:activator="{ on }">
								<v-text-field
									v-model="appointment.date"
									:label="$gettext('Date')"
									append-icon="event"
									readonly
									v-on="on"
									outlined
									class="px-2"
									:rules="dateRules"
									@change="edited"
								></v-text-field>
							</template>
							<v-date-picker color="primary" v-model="appointment.date" @input="dateMenu = false"></v-date-picker>
						</v-menu>
					</v-col>
					<v-col cols="12" sm="4">
						<v-menu
							ref="menu"
							v-model="timeMenu"
							:close-on-content-click="false"
							:nudge-right="40"
							:return-value.sync="appointment.time"
							transition="scale-transition"
							offset-y
							max-width="290px"
							min-width="290px"
						>
							<template v-slot:activator="{ on }">
								<v-text-field
									v-model="appointment.time"
									:label="$gettext('Time')"
									append-icon="access_time"
									readonly
									v-on="on"
									outlined
									class="px-2"
									:rules="timeRules"
									@change="edited"
								></v-text-field>
							</template>
							<v-time-picker
								v-if="timeMenu"
								v-model="appointment.time"
								full-width
								@click:minute="$refs.menu.save(appointment.time)"
								format="24hr"
								color="primary"
							></v-time-picker>
						</v-menu>
					</v-col>
					<v-col cols="12" sm="4">
						<v-select
							:items="duration"
							:label="$gettext('Duration')"
							v-model="appointment.duration"
							class="px-2"
							outlined
							:rules="durationRules"
							@change="edited"
						></v-select>
					</v-col>
					<v-col cols="12" sm="4">
						<v-select
							:items="establishmentsOptions"
							:label="$gettext('Establishment')"
							v-model="appointment.establishment_id"
							class="px-2"
							outlined
							:rules="establishmentRules"
							:no-data-text="$gettext('No Establishments available')"
							item-text="name"
							item-value="id"
							@change="edited"
						></v-select>
					</v-col>
					<v-col cols="12" sm="4">
						<v-select
							:items="officesOptions"
							:label="$gettext('Office')"
							v-model="appointment.location_id"
							class="px-2"
							outlined
							item-text="name"
							item-value="id"
							@change="edited"
							ref="selectAppointmentLocation"
						>
							<template v-slot:append-item>
								<v-divider class="my-1"></v-divider>
								<v-row no-gutters class="mx-1">
									<v-btn color="secondary" class="primary--text" depressed block @click="showNewAppointmentLocation = true; $refs.selectAppointmentLocation.blur();" :disabled="!appointment.establishment_id">
										<translate>New</translate>
									</v-btn>
								</v-row>
							</template>
						</v-select>
						<template v-if="showNewAppointmentLocation">
							<v-form ref="newAppointmentLocationForm">
								<v-row no-gutters class="pr-2">
									<v-text-field
										v-model="newAppointmentLocation"
										class="px-2"
										:label="$gettext('Name')"
										outlined
										:rules="newAppointmentLocationRules"
									>
										<template v-slot:append-outer>
											<v-btn x-small fab color="primary" class="mr-1" @click="createAppointmentLocation" :loading="loadingSaveAppointmentLocation" :disabled="!appointment.establishment_id">
												<v-icon>mdi-check</v-icon>
											</v-btn>
											<v-btn x-small fab color="primary" @click="showNewAppointmentLocation = false">
												<v-icon>mdi-close</v-icon>
											</v-btn>
										</template>
									</v-text-field>
								</v-row>
							</v-form>
						</template>
					</v-col>
					<v-col cols="12" sm="4">
						<v-select
							:items="appointmentTypes"
							:label="$gettext('Appointment Type')"
							v-model="appointment.type_id"
							class="px-2"
							outlined
							item-text="name"
							item-value="id"
							@change="edited"
							ref="selectAppointmentType"
						>
							<template v-slot:append-item>
								<v-divider class="my-1"></v-divider>
								<v-row no-gutters class="mx-1">
									<v-btn color="secondary" class="primary--text" depressed block @click="showNewAppointmentType = true; $refs.selectAppointmentType.blur();">
										<translate>New</translate>
									</v-btn>
								</v-row>
							</template>
						</v-select>
						<template v-if="showNewAppointmentType">
							<v-form ref="newAppointmentTypeForm">
								<v-row no-gutters class="pr-2">
									<v-text-field
										v-model="newAppointmentType"
										class="px-2"
										:label="$gettext('Name')"
										outlined
										:rules="newAppointmentTypeRules"
									>
										<template v-slot:append-outer>
											<v-btn x-small fab color="primary" class="mr-1" @click="createAppointmentType" :loading="loadingSaveAppointmentType">
												<v-icon>mdi-check</v-icon>
											</v-btn>
											<v-btn x-small fab color="primary" @click="showNewAppointmentType = false">
												<v-icon>mdi-close</v-icon>
											</v-btn>
										</template>
									</v-text-field>
								</v-row>
							</v-form>
						</template>
					</v-col>
					<v-col cols="12" sm="12">
						<v-text-field
							v-model="appointment.description"
							:label="$gettext('Description')"
							class="px-2"
							outlined
							@input="edited"
						/>
					</v-col>
					<v-col cols="12" sm="12">
						<v-text-field
							v-model="appointment.notes"
							:label="$gettext('Notes')"
							class="px-2"
							outlined
							hide-details
							@input="edited"
						/>
					</v-col>
					<v-col v-if="errorPlan" cols="12" sm="12">
						<v-alert v-model="errorPlan" color="red" icon="warning" class="mb-0 mt-2">
							<translate>You reached the maximum number of patients under your plan subscription</translate>
						</v-alert>
					</v-col>
					<v-col v-if="!calendar" cols="12" sm="12" class="mt-3">
						<v-btn v-if="edit" color="primary" @click="showDeleteAppointment = true">
							<v-icon left>mdi-delete</v-icon>
							<translate>Delete</translate>
							<DeleteConfirmationDialog v-model="showDeleteAppointment" item="appointment" @delete="deleteAppointment" />
						</v-btn>
						<v-btn color="primary" :loading="saving" absolute right class="mr-6" @click="editAppointment">
							<translate>Save</translate>
						</v-btn>
					</v-col>
				</v-row>
			</v-container>
		</v-form>
	</div>
</template>

<script>
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";
import axios from "axios";

export default {
	name: "NewAppointmentForm",
	props: {
		edit: { default: false },
		calendar: { default: false },
		appointment: {
			default: () => ({
				speciality_id: "",
				establishment_id: "",
				user_id: "",
				patient_id: "",
				type_id: null,
				location_id: null,
				description: "",
				date: "",
				time: "",
				duration: 15,
				location: "",
				notes: ""
			})
		},
		patients: {
			default: () => []
		},
		specialities: {
			default: () => []
		},
		professionals: {
			default: () => []
		},
		appointmentTypes: {
			default: () => []
		},
		appointmentLocations: {
			default: () => []
		}
	},
	components: {
		DeleteConfirmationDialog
	},
	data: () => ({
		valid: false,
		saving: false,
		search: null,
		loadingPatients: false,
		dateMenu: false,
		timeMenu: false,
		showDeleteAppointment: false,
		errorPlan: false,
		showNewAppointmentType: false,
		showNewAppointmentLocation: false,
		newAppointmentType: "",
		newAppointmentLocation: "",
		loadingSaveAppointmentType: false,
		loadingSaveAppointmentLocation: false
	}),
	methods: {
		edited() {
			this.$emit('edited');
		},
		setErrorPlan() {
			this.errorPlan = true;
		},
		patientFilter(item, queryText) {
			const itemText = item.name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
			const searchText = queryText.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

			if(!isNaN(searchText)) {
				if(item.phone && item.phone.indexOf(searchText) > -1) return true;
				if(item.fiscal && item.fiscal.indexOf(searchText) > -1) return true;
				return false;
			}

			const searchTextNames = searchText.split(" ");

			let exist = false;
			for(let i = 0; i < searchTextNames.length; i++) {
				if(searchTextNames[i]) {
					if(itemText.indexOf(searchTextNames[i]) > -1) {
						exist = true;
					}
					else return false;
				}
			}

			return exist;
			//return itemText.indexOf(searchText) > -1;
		},
		validate() {
			return this.$refs.form.validate();
		},
		clear() {
			return this.$refs.form.resetValidation();
		},
		newPatient(search) {
			console.log("SELECT NEW PATIENT");
			if (this.appointment.patient_id == -1) this.appointment.patient_id = 0;
			this.patients.forEach(v => {
				if (v.id == -1) {
					this.patients.splice(this.patients.indexOf(v), 1);
				}
			});
			this.patients.push({
				id: -1,
				name: search + " " + this.$gettext("(New Patient)"),
				real_name: search
			});
			this.appointment.patient_id = -1;
			this.$refs.patientField.blur();
		},
		editAppointment() {
			const formData = this.getForm();
			if (formData) {
				this.saving = true;
				const patientId = this.appointment.patient_id;
				if (this.edit) {
					formData.append("appointment_submit", true);
					axios
						.post(
							"/patient/" + patientId + "/appointment/" + this.appointment.id,
							formData
						)
						.then(response => {
							console.log(response.data);
							if (response.status === 200) {
								this.saving = false;
								this.$emit("close-form");
							}
						})
						.catch(error => console.log(error));
				} else {
					var appointmentsTab = "";
					if (this.$store.getters.hasRole("terapeut")) {
						appointmentsTab = "clinic";
					} else {
						appointmentsTab = "appointments";
					}
					axios
						.post("/patient/" + patientId + "/appointment/new", formData)
						.then(response => {
							console.log(response.data);
							if (response.status === 200) {
								if (response.data.result === "created") {
									this.saving = false;
									this.$router.push({
										name: "patient",
										params: { pid: patientId, tab: appointmentsTab }
									});
								} else {
									console.log(response.data.error);
								}
							}
						});
				}
			}
		},
		getForm() {
			if(this.validate()) {
				const formData = new FormData();
				formData.append("speciality_id", this.appointment.speciality_id);
				formData.append("establishment_id", this.appointment.establishment_id);
				if(this.appointment.patient_id != -1) formData.append("patient_id", this.appointment.patient_id);
				formData.append("user_id", this.appointment.user_id);
				if(this.appointment.type_id) formData.append("type_id", this.appointment.type_id);
				if(this.appointment.location_id) formData.append("location_id", this.appointment.location_id);
				formData.append("date", this.appointment.date);
				formData.append("time", this.appointment.time);
				formData.append("duration", this.appointment.duration);
				//formData.append("location", this.appointment.location);
				formData.append("description", this.appointment.description);
				formData.append("notes", this.appointment.notes);
				if(this.appointment.google_id) formData.append("google_id", this.appointment.google_id);
				return formData;
			} else return false;
		},
		deleteAppointment() {
			const patientId = this.appointment.patient_id;
			axios
				.post(
					"/patient/" +
						patientId +
						"/appointment/" +
						this.appointment.id +
						"/delete"
				)
				.then(response => {
					console.log(response.data);
					if (response.status === 200) {
						this.saving = false;
						if (response.data.result === "deleted") {
							var tab;
							if(this.$store.getters.hasRole('terapeut')) tab = "clinic";
							else tab = "appointments";
							this.$router.push({
								name: "patient",
								params: { pid: patientId, tab: tab }
							});
						}
					}
				})
				.catch(error => console.log(error));
		},
		init() {
			if (!this.calendar && !this.appointment.patient_id) {
				this.appointment.patient_id = this.$route.params.pid;
			}
			axios
				.get("/patient/" + this.appointment.patient_id + "/appointment/new")
				.then(response => {
					console.log(response.data);
					if (response.status === 200) {
						response.data.specialities.forEach(s => {
							this.specialities.push(s);
						})
						response.data.professionals.forEach(p => {
							this.professionals.push(p);
						})
						response.data.appointment_types.forEach(t => {
							this.appointmentTypes.push(t);
						})
						response.data.appointment_locations.forEach(t => {
							this.appointmentLocations.push(t);
						})
					}
				});
		},
		goToPatient() {
			if (this.appointment.patient_id && this.appointment.patient_id != -1) {
				this.$router.push({
					name: "patient",
					params: { pid: this.appointment.patient_id }
				});
			}
		},
		createAppointmentLocation() {
			if(this.$refs.newAppointmentLocationForm.validate()) {
				this.loadingSaveAppointmentLocation = true;
				let location = {
					name: this.newAppointmentLocation,
					establishment_id: this.appointment.establishment_id
				}
				axios
					.post("/appointment-location/new", location)
					.then(response => {
						console.log(response.data);
						this.appointmentLocations.push(response.data.appointment_location);
						let i = this.establishments.findIndex(e => e.id == this.appointment.establishment_id);
						this.$store.dispatch("ADD_ESTABLISHMENT_OFFICE", {establishment: this.establishments[i], office: response.data.appointment_location});
						this.appointment.location_id = response.data.appointment_location.id;
						this.newAppointmentLocation = "";
						this.showNewAppointmentLocation = false;
					})
					.catch(error => console.log(error))
					.finally(() => this.loadingSaveAppointmentLocation = false);
			}
		},
		createAppointmentType() {
			if(this.$refs.newAppointmentTypeForm.validate()) {
				this.loadingSaveAppointmentType = true;
				let type = {
					name: this.newAppointmentType,
					price: 0
				}
				axios
					.post("/appointment-type/new", type)
					.then(response => {
						console.log(response.data);
						this.appointmentTypes.push(response.data.appointment_type);
						this.appointment.type_id = response.data.appointment_type.id;
						this.newAppointmentType = "";
						this.showNewAppointmentType = false;
					})
					.catch(error => console.log(error))
					.finally(() => this.loadingSaveAppointmentType = false);
			}
		}
	},
	computed: {
		duration() {
			const minutesString = this.$gettext("Minutes");
			let list = [];
			let interval = this.$store.getters.appDurationInterval;
			for(let i=interval; i <= 120; i+=interval) {
				list.push({ text: i.toString() + " " + minutesString, value: i });
			}
			return list;
		},
		patientRules() {
			return [v => !!v || this.$gettext("Patient is required")];
		},
		specialityRules() {
			return [v => !!v || this.$gettext("Speciality is required")];
		},
		establishmentRules() {
			return [v => !!v || this.$gettext("Establishment is required")];
		},
		professionalRules() {
			return [v => !!v || this.$gettext("Professional is required")];
		},
		dateRules() {
			return [v => !!v || this.$gettext("Date is required")];
		},
		timeRules() {
			return [v => !!v || this.$gettext("Time is required")];
		},
		durationRules() {
			return [v => !!v || this.$gettext("Duration is required")];
		},
		newAppointmentLocationRules() {
			return [v => !!v || this.$gettext("Name is required"), v => this.officesOptions.filter(l => l.name == v).length == 0 || this.$gettext("Name already exists.")];
		},
		newAppointmentTypeRules() {
			return [v => !!v || this.$gettext("Name is required"), v => this.appointmentTypes.filter(t => t.name == v).length == 0 || this.$gettext("Name already exists.")];
		},
		specialitiesOptions() {
			return this.specialities;
		},
		professionalsOptions() {
			if (
				this.appointment.speciality_id &&
				this.appointment.speciality_id != ""
			) {
				var profs = [];
				this.professionals.forEach(p => {
					p.specialities.forEach(s => {
						if (s.id == this.appointment.speciality_id) profs.push(p);
					});
				});
				return profs;
			} else {
				return this.professionals;
			}
		},
		establishmentsOptions() {
			if (this.appointment.user_id && this.appointment.user_id != "") {
				for(let i = 0; this.professionalsOptions.length > i; i++) {
					if(this.professionalsOptions[i].id == this.appointment.user_id) {
						return this.professionalsOptions[i].establishments;
					}
				}
			}
			return this.establishments;
		},
		officesOptions() {
			if(this.appointment.establishment_id) {
				let i = this.establishments.findIndex(e => e.id == this.appointment.establishment_id);
				return this.establishments[i].offices;
			} else return this.appointmentLocations;
		},
		establishments() {
			return this.$store.getters.establishments;
		}
	},
	mounted() {
		if (!this.calendar) {
			this.init();
		}
	},
	watch: {
		establishmentsOptions(list) {
			if(!this.calendar && list.length == 1) {this.appointment.establishment_id = list[0].id}
		}
	}
};
</script>